import React, { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import EditorJS from "@editorjs/editorjs";
import Header from "@editorjs/header";
import List from "@editorjs/list";
import Quote from "@editorjs/quote";
import Delimiter from "@editorjs/delimiter";
import { fetchGalleries } from "../lib/galleries";
import { fetchForms } from "../lib/forms";
import { fetchLinkOptions } from "../lib/page-links";
import type { MediaItem } from "../lib/media";
import "./block-editor.css";
import { ContactFormTool } from "./blocks/contact-form-tool.js";
import { CtaTool } from "./blocks/cta-tool.js";
import { ColorInlineTool, FontSizeInlineTool } from "./blocks/inline-style-tools.js";
import { EventsTool } from "./blocks/events-tool.js";
import { BookingTool } from "./blocks/booking-tool.js";
import { MenuTool } from "./blocks/menu-tool.js";
import { GalleryTool } from "./blocks/gallery-tool.js";
import { FormTool } from "./blocks/form-tool.js";
import { ImageTool } from "./blocks/image-tool.js";
import { TestimonialsTool } from "./blocks/testimonials-tool.js";
import { MapTool } from "./blocks/map-tool.js";
import { BarListTool } from "./blocks/bar-list-tool.js";
import type { ApiClient } from "../lib/api";

type BlockData = Record<string, any>;

export interface ColumnBlocksEditorHandle {
  /** Serialize the column's current blocks (safe to call any time). */
  getBlocks(): Promise<BlockData[]>;
}

interface ColumnBlocksEditorProps {
  /** Initial document only — the editor owns its content after mount. */
  blocks: BlockData[];
  /** Change signal (no payload); content is pulled via getBlocks() on demand. */
  onChange?: () => void;
  api: ApiClient;
  placeholder?: string;
  /** Media-picker bridge for the image block (see useMediaPicker). */
  pickImage: () => Promise<MediaItem | null>;
  /** Offer the events listing block (only when the events module is active). */
  eventsEnabled?: boolean;
  /** Offer the booking widget block (only when the booking module is active). */
  bookingEnabled?: boolean;
  /** Offer the menu block (only when the restaurant_menu module is active). */
  menuEnabled?: boolean;
  /** Active module slugs — module pages join the button block's link picker. */
  activeModules?: string[];
  /** Tenant palette colours offered by the button block's colour picker. */
  palette?: string[];
  /** Holder classes; defaults to the standalone bordered-card look. */
  holderClassName?: string;
}

/**
 * Thin React wrapper around one vanilla Editor.js instance for a single
 * section column. Editor.js owns its DOM, so this mounts once (init in
 * useEffect, destroy on unmount) and treats `blocks` as the initial document
 * only. Serialization happens on demand via the imperative getBlocks() —
 * never on keystroke.
 */
export const ColumnBlocksEditor = forwardRef<ColumnBlocksEditorHandle, ColumnBlocksEditorProps>(
  function ColumnBlocksEditor(
    { blocks, onChange, api, placeholder, pickImage, eventsEnabled, bookingEnabled, menuEnabled, activeModules, palette, holderClassName },
    ref
  ) {
    const holderRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<EditorJS | null>(null);
    // Keep the latest onChange/pickImage/modules/palette without re-initializing.
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const pickImageRef = useRef(pickImage);
    pickImageRef.current = pickImage;
    const activeModulesRef = useRef(activeModules);
    activeModulesRef.current = activeModules;
    const paletteRef = useRef(palette);
    paletteRef.current = palette;
    const initialBlocksRef = useRef(blocks);

    useImperativeHandle(ref, () => ({
      async getBlocks() {
        const editor = editorRef.current;
        if (!editor) return initialBlocksRef.current ?? [];
        await editor.isReady;
        return (await editor.save()).blocks;
      },
    }), []);

    useEffect(() => {
      if (!holderRef.current) return undefined;

      // Each editor instance gets its own child mount node. Editor.js destroy()
      // empties its holder, and the cleanup below is deferred on isReady — under
      // StrictMode's dev-only mount→unmount→remount, the first instance's
      // deferred destroy() would otherwise wipe the second instance's DOM out
      // of the shared holder (an empty editor, no error).
      const mount = document.createElement("div");
      holderRef.current.appendChild(mount);

      const editor = new EditorJS({
        holder: mount,
        data: { blocks: (initialBlocksRef.current ?? []) as any[] },
        placeholder: placeholder ?? "Start writing…",
        minHeight: 160,
        tools: {
          // Inline text/heading formatting: colour (tenant palette / custom)
          // and font size (token type-scale steps). Available on every tool
          // with an inline toolbar (paragraph, header, list, quote).
          textColor: {
            class: ColorInlineTool as any,
            config: { getPalette: () => paletteRef.current ?? [] },
          },
          fontSize: { class: FontSizeInlineTool as any },
          header: {
            class: Header as any,
            inlineToolbar: true,
            config: { levels: [1, 2, 3, 4], defaultLevel: 2 },
          },
          list: { class: List as any, inlineToolbar: true },
          quote: { class: Quote as any, inlineToolbar: true },
          delimiter: Delimiter as any,
          // Call-to-action buttons (side by side): link picker (tenant +
          // module pages) and background colour per button (tenant palette /
          // custom, text colour derived).
          cta: {
            class: CtaTool as any,
            config: {
              listLinkOptions: () =>
                fetchLinkOptions(api, activeModulesRef.current ?? []),
              getPalette: () => paletteRef.current ?? [],
            },
          },
          // Media-library-backed image block (browse the library or upload);
          // stores the same { file: { key, url }, caption } shape as before.
          image: {
            class: ImageTool as any,
            config: {
              pickImage: async () => {
                const item = await pickImageRef.current();
                return item
                  ? { key: item.key, url: item.url ?? null, caption: item.caption }
                  : null;
              },
            },
          },
          // Gallery/form embed portal-managed resources by id; contactForm is a
          // legacy contentless marker (registered so existing blocks survive an
          // edit, but absent from the toolbox). All render as real components
          // on the live site.
          gallery: {
            class: GalleryTool,
            config: { listGalleries: () => fetchGalleries(api) },
          },
          form: {
            class: FormTool,
            config: { listForms: () => fetchForms(api) },
          },
          contactForm: ContactFormTool,
          // Quote cards (grid or swipeable carousel on the live site).
          testimonials: TestimonialsTool as any,
          // OpenStreetMap embed (search via Nominatim or manual coordinates).
          map: MapTool as any,
          // Labels with long accent rules (highlighted focus-areas pattern).
          barList: {
            class: BarListTool as any,
            config: { getPalette: () => paletteRef.current ?? [] },
          },
          // Events listing block — only offered when the module is active
          // (the block stores display settings; the live site fetches the
          // events at runtime from R2).
          ...(eventsEnabled ? { events: EventsTool } : {}),
          // Booking widget block — only offered when the module is active (the
          // block stores nothing; the live site drives the public booking API).
          ...(bookingEnabled ? { booking: BookingTool } : {}),
          // Menu block — only offered when the restaurant_menu module is active
          // (the block stores { columns }; the live site renders the menus from
          // its build-time feed).
          ...(menuEnabled ? { menu: MenuTool } : {}),
        },
        onChange() {
          onChangeRef.current?.();
        },
      });

      editorRef.current = editor;

      return () => {
        // destroy() is only safe once the editor is ready.
        editor.isReady
          .then(() => editor.destroy())
          .catch(() => {})
          .finally(() => mount.remove());
        editorRef.current = null;
      };
    }, [api, placeholder, eventsEnabled, bookingEnabled, menuEnabled]);

    return (
      <div
        ref={holderRef}
        className={holderClassName ?? "rounded-md border bg-background px-3 py-2 text-sm [&_.codex-editor__redactor]:pb-0"}
      />
    );
  }
);
